import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState } from 'react';
import { createFilterOptions } from '@material-ui/lab/useAutocomplete';
import { Checkmark } from '../../icons';
import { BottomSheetContentLayout } from '../BottomSheet';
import { CircularLoader } from '../CircularLoader';
import { useCloseHostingBottomSheetPage } from '../OverlayContext/hooks/useCloseHostingBottomSheetPage';
import { Searchbox } from '../Searchbox';
import { Typography } from '../Typography';
import styles from './AutocompleteBottomSheet.module.scss';
import { useAsyncOptions } from './useAsyncOptions';
export function AutocompleteBottomSheet(props) {
    const { options, getOptionLabel = (option)=>String(option), renderOption, filterOptions, getOptionDisabled, getOptionSelected, onChange, value, multiple, freeSolo, filterSelectedOptions, loading, noOptionsText, label, testId, disableCloseOnSelect, inputValue, onInputChange, loadOptions, loadOptionsDebounceMs, searchPlaceholder, doneButtonLabel, popoverFooter, searchBiName } = props;
    const closeHostingPage = useCloseHostingBottomSheetPage();
    const { options: asyncOptions, loading: asyncLoading, search } = useAsyncOptions(loadOptions, loadOptionsDebounceMs);
    const isAsync = loadOptions != null;
    const deriveInitialSearchValue = ()=>{
        if (inputValue != null) return inputValue;
        if (value == null || multiple) return '';
        return getOptionLabel(value);
    };
    const [searchValue, setSearchValue] = useState(deriveInitialSearchValue);
    const [hasUserTyped, setHasUserTyped] = useState(false);
    const [selectedValue, setSelectedValue] = useState(value);
    const isOptionSelected = (option)=>{
        if (selectedValue == null) return false;
        if (multiple && Array.isArray(selectedValue)) {
            return selectedValue.some((v)=>getOptionSelected ? getOptionSelected(option, v) : option === v);
        }
        return getOptionSelected ? getOptionSelected(option, selectedValue) : option === selectedValue;
    };
    const filteredOptions = (()=>{
        if (isAsync) {
            return asyncOptions;
        }
        let opts = options;
        if (filterSelectedOptions && multiple && Array.isArray(selectedValue)) {
            opts = opts.filter((option)=>!isOptionSelected(option));
        }
        const effectiveInputValue = !hasUserTyped || !searchValue ? '' : searchValue;
        if (filterOptions) {
            return filterOptions(opts, {
                inputValue: effectiveInputValue,
                getOptionLabel
            });
        }
        if (effectiveInputValue === '') {
            return opts;
        }
        const filter = createFilterOptions();
        return filter(opts, {
            inputValue: effectiveInputValue,
            getOptionLabel
        });
    })();
    const showDoneButton = !!multiple || !!freeSolo;
    const handleOptionClick = (option)=>{
        if (multiple) {
            const currentArr = Array.isArray(selectedValue) ? selectedValue : [];
            const alreadySelected = currentArr.some((v)=>getOptionSelected ? getOptionSelected(option, v) : option === v);
            const newValue = alreadySelected ? currentArr.filter((v)=>!(getOptionSelected ? getOptionSelected(option, v) : option === v)) : [
                ...currentArr,
                option
            ];
            setSelectedValue(newValue);
            if (onChange) {
                onChange({}, newValue, alreadySelected ? 'remove-option' : 'select-option');
            }
        } else {
            setSelectedValue(option);
            if (onChange) {
                onChange({}, option, 'select-option');
            }
            if (!disableCloseOnSelect) {
                closeHostingPage();
            }
        }
    };
    const handleDoneClick = ()=>{
        if (freeSolo && !multiple && searchValue) {
            if (onChange) {
                onChange({}, searchValue, 'create-option');
            }
        }
        closeHostingPage();
    };
    const handleInputChange = function handleInputChange(newValue, event) {
        let reason = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : 'input';
        setHasUserTyped(true);
        setSearchValue(newValue);
        if (isAsync) {
            search(newValue);
        }
        onInputChange === null || onInputChange === void 0 ? void 0 : onInputChange(event !== null && event !== void 0 ? event : {}, newValue, reason);
    };
    const resolvedSearchBiName = searchBiName !== null && searchBiName !== void 0 ? searchBiName : 'search';
    const isLoading = loading || asyncLoading;
    const hasOptions = filteredOptions.length > 0;
    const showNoOptions = isAsync ? !isLoading && hasUserTyped && !!searchValue && !hasOptions : !isLoading && !hasOptions;
    return /*#__PURE__*/ _jsxs(BottomSheetContentLayout, {
        header: {
            title: label,
            paddingBottom: 0,
            gutter: 'm',
            subtitle: /*#__PURE__*/ _jsx(Searchbox, {
                bi: {
                    name: resolvedSearchBiName
                },
                variant: "textfield",
                fluidWidth: true,
                autoFocus: true,
                placeholder: searchPlaceholder,
                testId: testId ? "".concat(testId, "-mobile-searchbox") : undefined,
                value: searchValue,
                onChange: (e)=>handleInputChange(e.target.value, e),
                onClear: ()=>handleInputChange('', undefined, 'clear')
            })
        },
        footer: showDoneButton ? {
            content: popoverFooter,
            primaryAction: {
                text: doneButtonLabel !== null && doneButtonLabel !== void 0 ? doneButtonLabel : 'Done',
                onClick: handleDoneClick,
                bi: {
                    name: 'done'
                }
            },
            paddingTop: 8
        } : popoverFooter ? {
            content: popoverFooter
        } : undefined,
        children: [
            isLoading && /*#__PURE__*/ _jsx("div", {
                className: styles.loaderContainer,
                "data-testid": testId ? "".concat(testId, "-mobile-loading") : undefined,
                children: /*#__PURE__*/ _jsx(CircularLoader, {})
            }),
            showNoOptions && noOptionsText && /*#__PURE__*/ _jsx("div", {
                className: styles.noOptions,
                "data-testid": testId ? "".concat(testId, "-mobile-no-options") : undefined,
                children: /*#__PURE__*/ _jsx(Typography, {
                    children: noOptionsText
                })
            }),
            filteredOptions.map((option, index)=>{
                const selected = isOptionSelected(option);
                const disabled = getOptionDisabled ? getOptionDisabled(option) : false;
                return /*#__PURE__*/ _jsxs("div", {
                    "data-testid": testId ? "".concat(testId, "-mobile-option-").concat(index) : undefined,
                    className: "".concat(styles.option, " ").concat(disabled ? styles.disabled : ''),
                    onClick: !disabled ? ()=>handleOptionClick(option) : undefined,
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: styles.optionContent,
                            children: renderOption ? renderOption(option, {
                                selected,
                                inputValue: searchValue
                            }) : /*#__PURE__*/ _jsx(Typography, {
                                children: getOptionLabel(option)
                            })
                        }),
                        selected && /*#__PURE__*/ _jsx(Checkmark, {
                            testId: testId ? "".concat(testId, "-mobile-option-").concat(index, "-checkmark") : undefined,
                            size: 20
                        })
                    ]
                }, "".concat(index, "-").concat(getOptionLabel(option)));
            })
        ]
    });
}
